import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import getSubproductDetailsTotalExpense from 'services/products/calculations/getSubproductDetailsTotalExpense';
import getSubproductDetailsTotalSellingPrice from 'services/products/calculations/getSubproductDetailsTotalSellingPrice';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';

import {
  CALCULATE_PRICE_FROM_DETAILS,
  EXPENSE,
  EXPENSE_UNIT,
  QUANTITY,
  SELLING_PRICE,
  SELLING_PRICE_UNIT,
  WASTE_PERCENTAGE,
} from 'services/products/productConfigurationAttributeTypes';

import ISubproduct from '../../ISubproduct';

/*
 * A material priced from its details ("calculate price from details"). The details are
 * calculated like any material rows, with their own quantities and waste, and together
 * they make the price of ONE unit of the material. The material quantity multiplies both
 * the expense and the selling price, and the waste of the material raises both: the
 * expense through the usual waste of the row, the selling price here.
 *
 * Saved offers must not change: a material saved before this (its selling price a sum,
 * without a unit) keeps the earlier calculation, also when it is edited. A material
 * gets this calculation when "calculate price from details" is ticked: its selling price
 * then gets the unit of its expense, which is saved with it.
 */

/**
 * The prices calculated from the details are kept with this many decimals, so a quantity
 * multiplying them does not grow a rounding into a visible difference; the calculation
 * window shows them in cents (see showInCents)
 */
export const HIDDEN_DECIMALS = 6;

// The price of one unit of a copied material, as many decimals as the form loads
const COPY_DECIMALS = 10;

/** Whether the material is priced per unit from its details (and not the earlier way) */
export const isPricedPerUnitFromDetails = (subproduct: ISubproduct): boolean =>
  subproduct.expenseUnit !== '' &&
  subproduct.sellingPriceUnit === subproduct.expenseUnit;

/** The waste of the material as a factor, e.g. 10 % -> 1,1 */
const getWasteFactor = (subproduct: ISubproduct): number =>
  1 + numericStringToFloat(subproduct.wastePercentage) / 100;

// The fields of a detail that change the expense or the selling price of the material in
// the earlier calculation; a price the user has typed for the material is kept otherwise
const EXPENSE_FIELDS = [
  CALCULATE_PRICE_FROM_DETAILS,
  EXPENSE,
  EXPENSE_UNIT,
  QUANTITY,
  WASTE_PERCENTAGE,
];
const SELLING_PRICE_FIELDS = [
  CALCULATE_PRICE_FROM_DETAILS,
  QUANTITY,
  SELLING_PRICE,
  SELLING_PRICE_UNIT,
];

/**
 * The material with its expense and selling price from its details. With the changed
 * field of a detail, a material priced the earlier way gets only the price that field
 * changes, as before; without it, both.
 */
const priceMaterialFromDetails = (
  subproduct: ISubproduct,
  updatedField?: string
): ISubproduct => {
  if (!subproduct.calculatePriceFromDetails) {
    return subproduct;
  }

  const expense = formatFloatForDisplay(
    getSubproductDetailsTotalExpense(subproduct.details),
    HIDDEN_DECIMALS
  );
  const detailsSellingPrice = getSubproductDetailsTotalSellingPrice(
    subproduct.details
  );

  // The earlier calculation of a saved material: the sums of the details as they are,
  // rounded to cents as before
  if (!isPricedPerUnitFromDetails(subproduct)) {
    const updates = (fields: string[]) =>
      updatedField === undefined || fields.includes(updatedField);

    return {
      ...subproduct,
      expense: updates(EXPENSE_FIELDS)
        ? formatFloatForDisplay(
            getSubproductDetailsTotalExpense(subproduct.details)
          )
        : subproduct.expense,
      sellingPrice: updates(SELLING_PRICE_FIELDS)
        ? formatFloatForDisplay(detailsSellingPrice)
        : subproduct.sellingPrice,
    };
  }

  return {
    ...subproduct,
    expense,
    sellingPrice: formatFloatForDisplay(
      detailsSellingPrice * getWasteFactor(subproduct),
      HIDDEN_DECIMALS
    ),
  };
};

/** The selling price the details give the material (with the calculation it uses) */
export const getSellingPriceFromDetails = (subproduct: ISubproduct): number => {
  const detailsSellingPrice = getSubproductDetailsTotalSellingPrice(
    subproduct.details
  );

  return isPricedPerUnitFromDetails(subproduct)
    ? detailsSellingPrice * getWasteFactor(subproduct)
    : detailsSellingPrice;
};

/**
 * Whether a material priced from its details has a selling price of its own, typed by
 * the user (or copied as such): it differs from the price of its details by a cent or more.
 */
export const isSellingPriceTyped = (subproduct: ISubproduct): boolean =>
  subproduct.calculatePriceFromDetails &&
  Math.abs(
    numericStringToFloat(subproduct.sellingPrice) -
      getSellingPriceFromDetails(subproduct)
  ) >= 0.005;

/** The material with the selling price of its details instead of a typed one */
export const withSellingPriceFromDetails = (
  subproduct: ISubproduct
): ISubproduct => ({
  ...subproduct,
  sellingPrice: formatFloatForDisplay(
    getSellingPriceFromDetails(subproduct),
    HIDDEN_DECIMALS
  ),
});

/** A material starting to be priced from its details gets the calculation per unit */
export const startPricingFromDetails = (subproduct: ISubproduct): ISubproduct =>
  priceMaterialFromDetails({
    ...subproduct,
    calculatePriceFromDetails: true,
    sellingPriceUnit: subproduct.expenseUnit,
  });

/**
 * A material copied as the base of a new calculation (a copied alternative or a position
 * added as an alternative) gets the calculation per unit; the original keeps its own.
 * The copy is priced exactly as the original: the selling price of the row (a sum, often
 * typed by the user) is divided by the quantity of the material into the price of one
 * unit. Later changes of its details or waste price it the new way.
 */
export const priceCopiedMaterial = (subproduct: ISubproduct): ISubproduct => {
  const quantity = numericStringToFloat(subproduct.quantity);

  if (
    !subproduct.calculatePriceFromDetails ||
    subproduct.expenseUnit === '' ||
    isPricedPerUnitFromDetails(subproduct) ||
    !(quantity > 0)
  ) {
    return subproduct;
  }

  return {
    ...subproduct,
    // As many decimals as the form keeps, so even a large quantity gives back the sum
    sellingPrice: formatFloatForDisplay(
      numericStringToFloat(subproduct.sellingPrice) / quantity,
      COPY_DECIMALS
    ),
    sellingPriceUnit: subproduct.expenseUnit,
  };
};

/**
 * What the totals of the details are multiplied by in the calculation window, so the
 * details add up to the totals of the material: the position quantity, the material
 * quantity (when the material is priced per unit) and the waste of the material. A
 * material priced the earlier way keeps its earlier detail totals.
 */
export const getDetailTotalsFactor = (
  subproduct: ISubproduct,
  positionQuantity: number
): number =>
  subproduct.calculatePriceFromDetails && isPricedPerUnitFromDetails(subproduct)
    ? positionQuantity *
      getSellingPriceQuantityFactor(
        subproduct.expenseUnit,
        numericStringToFloat(subproduct.quantity)
      ) *
      getWasteFactor(subproduct)
    : positionQuantity;

export default priceMaterialFromDetails;
